/**
 * MafiaGame — Main container component
 *
 * Orchestrates lobby → role reveal → night/day cycle → game over.
 * Wraps in ErrorBoundary, uses useAuthentication, responsive layout.
 *
 * Requirements: 1.1, 1.7, 2.7, 3.4, 3.6, 7.6, 7.7, 11.4–11.6, 13.1,
 *               19.1–19.6, 22.1, 22.4, 22.5, 22.8, 23.3, 24.1, 24.5
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { useAuthentication } from '../../hooks/useAuthentication';
import { GameSessionManager } from '../../services/GameSessionManager';
import { MultiplayerMafiaService } from '../services/MultiplayerMafiaService';
import { WebSocketService } from '../../services/WebSocketService';
import { NotificationService } from '../../services/NotificationService';
import { GameResult } from '../../services/multiplayer/GenericMultiplayerService';
import {
  MafiaRole,
  Team,
  GamePhaseType,
  MafiaGameConfig,
  DEFAULT_MAFIA_CONFIG,
  LobbyPlayer,
  PlayerVisibleState,
  MafiaGameResult,
  InvestigationResult,
  ChatMessage,
  GameEvent,
  VisiblePlayer,
} from '../types';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { MafiaLobby } from './MafiaLobby';
import { MafiaRoleReveal } from './MafiaRoleReveal';
import { MafiaBoard } from './MafiaBoard';
import { MafiaGameOver } from './MafiaGameOver';
import { MafiaInstructions } from './MafiaInstructions';
import './MafiaGame.css';

export interface MafiaGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

type MafiaView = 'modeSelect' | 'lobby' | 'roleReveal' | 'game' | 'gameOver';

const MafiaGameInner: React.FC<MafiaGameProps> = ({ onExit, initialRoomCode }) => {
  const { isAuthenticated } = useAuthentication();

  // View state
  const [view, setView] = useState<MafiaView>(initialRoomCode ? 'lobby' : 'modeSelect');
  const [showInstructions, setShowInstructions] = useState(false);

  // Multiplayer service
  const [multiplayerService] = useState<MultiplayerMafiaService>(
    () => new MultiplayerMafiaService(WebSocketService.getInstance())
  );
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');
  const hasAttemptedJoin = useRef(false);

  // Lobby state
  const [roomCode, setRoomCode] = useState(initialRoomCode || '');
  const [isHost, setIsHost] = useState(false);
  const [config, setConfig] = useState<MafiaGameConfig>({ ...DEFAULT_MAFIA_CONFIG });
  const [lobbyPlayers, setLobbyPlayers] = useState<LobbyPlayer[]>([]);
  const [myPlayerId] = useState(`player_${Date.now()}_${Math.random().toString(36).substring(7)}`);

  // Role state
  const [myRole, setMyRole] = useState<MafiaRole>('villager');
  const [myTeam, setMyTeam] = useState<Team>('village');
  const [mafiaMembers, setMafiaMembers] = useState<string[]>([]);

  // Game state
  const [currentPhase, setCurrentPhase] = useState<GamePhaseType>('lobby');
  const [phaseTimeRemaining, setPhaseTimeRemaining] = useState(0);
  const [gameResult, setGameResult] = useState<MafiaGameResult | null>(null);
  const [investigationResults, setInvestigationResults] = useState<InvestigationResult[]>([]);
  const [myNightSelection, setMyNightSelection] = useState<string | undefined>();
  const [myDayVote, setMyDayVote] = useState<string | null | undefined>();

  // Player visible state for the board
  const [gameState, setGameState] = useState<PlayerVisibleState | null>(null);

  // =========================================================================
  // Multiplayer event handlers setup
  // =========================================================================
  useEffect(() => {
    multiplayerService.onGameStarted(() => {
      // Room created/joined successfully
    });

    multiplayerService.onGameOver((result: GameResult) => {
      const message = result.result === 'win'
        ? `🎉 ${result.winner} wins!`
        : 'Game over!';
      NotificationService.getInstance().sendInfoNotification(message);
    });

    multiplayerService.onPlayerDisconnected((playerId) => {
      NotificationService.getInstance().sendWarningNotification(
        'A player disconnected. Waiting for reconnection...'
      );
    });

    multiplayerService.onPlayerConnected((playerId) => {
      NotificationService.getInstance().sendSuccessNotification('Player reconnected!');
    });

    multiplayerService.onGameStateReceived((gameState: any) => {
      if (gameState) {
        setGameState(gameState);
      }
    });

    multiplayerService.onConnectionStatus((status) => {
      setConnectionStatus(status);
      if (status === 'reconnecting') {
        NotificationService.getInstance().sendWarningNotification('Connection lost. Reconnecting...');
      } else if (status === 'connected') {
        NotificationService.getInstance().sendSuccessNotification('Connected!');
      } else if (status === 'disconnected') {
        NotificationService.getInstance().sendErrorNotification('Disconnected from server');
      }
    });

    multiplayerService.onError((error: string) => {
      NotificationService.getInstance().sendErrorNotification(`Multiplayer error: ${error}`);
    });

    multiplayerService.onRoleAssigned((role, team, mafiaMembers) => {
      setMyRole(role);
      setMyTeam(team);
      if (mafiaMembers) setMafiaMembers(mafiaMembers);
    });

    multiplayerService.onPhaseChanged((phase, phaseData) => {
      if (phase.type) {
        setCurrentPhase(phase.type as GamePhaseType);
      }
    });

    multiplayerService.onLobbyUpdate((players) => {
      setLobbyPlayers(players);
    });

    multiplayerService.onConfigUpdate((newConfig) => {
      setConfig(newConfig);
    });

    multiplayerService.onGameOverMafia((result) => {
      setGameResult(result);
      setView('gameOver');
    });

    multiplayerService.onReconnectionFailed(() => {
      NotificationService.getInstance().sendErrorNotification(
        'Unable to reconnect. Please return to the menu and try again.'
      );
    });
  }, [multiplayerService]);

  // =========================================================================
  // Deep link room code handling
  // =========================================================================
  useEffect(() => {
    if (initialRoomCode && !hasAttemptedJoin.current) {
      hasAttemptedJoin.current = true;

      multiplayerService.joinRoom(initialRoomCode)
        .then(() => {
          setRoomCode(initialRoomCode);
          setIsHost(false);
          setView('lobby');
        })
        .catch((error: any) => {
          NotificationService.getInstance().sendErrorNotification(
            `Unable to join room ${initialRoomCode}. ${error.message || 'Please check the room code.'}`
          );
          setView('modeSelect');
        });
    }
  }, [initialRoomCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mode selection handlers
  const handleCreateRoom = useCallback(() => {
    multiplayerService.createRoom()
      .then((room) => {
        setRoomCode(room.code);
        setIsHost(true);
        setLobbyPlayers([{
          id: myPlayerId,
          name: 'You',
          isHost: true,
          isReady: true,
          connectionStatus: 'connected',
        }]);
        setView('lobby');

        try {
          GameSessionManager.getInstance().startGameSession('mafia');
        } catch { /* non-critical */ }
      })
      .catch((error: any) => {
        NotificationService.getInstance().sendErrorNotification(
          `Failed to create room: ${error.message || 'Unknown error'}`
        );
      });
  }, [myPlayerId, multiplayerService]);

  const handleJoinRoom = useCallback((code: string) => {
    multiplayerService.joinRoom(code)
      .then(() => {
        setRoomCode(code);
        setIsHost(false);
        setLobbyPlayers([{
          id: myPlayerId,
          name: 'You',
          isHost: false,
          isReady: false,
          connectionStatus: 'connected',
        }]);
        setView('lobby');
      })
      .catch((error: any) => {
        NotificationService.getInstance().sendErrorNotification(
          `Unable to join room ${code}. ${error.message || 'Please check the room code.'}`
        );
      });
  }, [myPlayerId, multiplayerService]);

  const handleConfigChange = useCallback((newConfig: MafiaGameConfig) => {
    setConfig(newConfig);
  }, []);

  const handleStartGame = useCallback(() => {
    // Transition to role reveal
    setView('roleReveal');
    setMyRole('villager');
    setMyTeam('village');
  }, []);

  const handleReady = useCallback((ready: boolean) => {
    setLobbyPlayers((prev) =>
      prev.map((p) => (p.id === myPlayerId ? { ...p, isReady: ready } : p))
    );
  }, [myPlayerId]);

  const handleLeave = useCallback(() => {
    multiplayerService.leaveGame();
    setView('modeSelect');
    setRoomCode('');
    setLobbyPlayers([]);
  }, [multiplayerService]);

  const handleAcknowledgeRole = useCallback(() => {
    setView('game');
    setCurrentPhase('night');
    setPhaseTimeRemaining(config.nightDuration);

    // Create initial game state
    const initialState: PlayerVisibleState = {
      roomCode,
      config,
      players: lobbyPlayers.map((p) => ({
        id: p.id,
        name: p.name,
        status: 'alive' as const,
        connectionStatus: p.connectionStatus,
        isHost: p.isHost,
      })),
      hostId: lobbyPlayers.find((p) => p.isHost)?.id || myPlayerId,
      phase: { type: 'night', nightNumber: 1 },
      cycleNumber: 1,
      phaseTimeRemaining: config.nightDuration,
      myRole,
      myTeam,
      mafiaMembers: myRole === 'mafia' ? mafiaMembers : undefined,
      votes: new Map(),
      dayChatMessages: [],
      mafiaChatMessages: myRole === 'mafia' ? [] : undefined,
      eventLog: [{
        id: 'evt_start',
        type: 'game_start',
        message: 'The game has begun!',
        timestamp: Date.now(),
        cycleNumber: 1,
      }],
      gameStatus: { type: 'inProgress' },
    };
    setGameState(initialState);
  }, [config, roomCode, lobbyPlayers, myPlayerId, myRole, myTeam, mafiaMembers]);

  // Game action handlers
  const handleMafiaVote = useCallback((targetId: string) => {
    setMyNightSelection(targetId);
  }, []);

  const handleDoctorProtect = useCallback((targetId: string) => {
    setMyNightSelection(targetId);
  }, []);

  const handleDetectiveInvestigate = useCallback((targetId: string) => {
    setMyNightSelection(targetId);
  }, []);

  const handleDayVote = useCallback((targetId: string | null) => {
    setMyDayVote(targetId);
  }, []);

  const handleChatMessage = useCallback((message: string) => {
    multiplayerService.sendChatMessage(message, 'day');
  }, [multiplayerService]);

  const handlePlayAgain = useCallback(() => {
    multiplayerService.leaveGame();
    setView('lobby');
    setGameResult(null);
    setInvestigationResults([]);
    setMyNightSelection(undefined);
    setMyDayVote(undefined);
    setGameState(null);
  }, [multiplayerService]);

  const handleExit = useCallback(() => {
    if (onExit) onExit();
  }, [onExit]);

  // Render based on current view
  const renderView = () => {
    switch (view) {
      case 'modeSelect': {
        const mafiaConfig: GameModeConfig = {
          gameName: 'Mafia',
          gameIcon: '🎭',
          subtitle: 'Social deduction multiplayer',
          availableModes: ['online'],
        };

        return (
          <UniversalGameModeSelector
            config={mafiaConfig}
            onStart={() => {}}
            onJoinRoom={handleJoinRoom}
            onCreateRoom={handleCreateRoom}
            isAuthenticated={isAuthenticated}
            guestGamesRemaining={
              isAuthenticated ? undefined : GameSessionManager.getInstance().getRemainingFreeGames()
            }
          />
        );
      }

      case 'lobby':
        return (
          <MafiaLobby
            players={lobbyPlayers}
            config={config}
            isHost={isHost}
            roomCode={roomCode}
            onConfigChange={handleConfigChange}
            onStartGame={handleStartGame}
            onReady={handleReady}
            onLeave={handleLeave}
            myPlayerId={myPlayerId}
            guestGamesRemaining={
              isAuthenticated ? undefined : GameSessionManager.getInstance().getRemainingFreeGames()
            }
          />
        );

      case 'roleReveal':
        return (
          <MafiaRoleReveal
            role={myRole}
            team={myTeam}
            mafiaMembers={myRole === 'mafia' ? mafiaMembers : undefined}
            onAcknowledge={handleAcknowledgeRole}
          />
        );

      case 'game':
        if (!gameState) return null;
        return (
          <MafiaBoard
            gameState={gameState}
            currentPhase={currentPhase}
            playerId={myPlayerId}
            playerRole={myRole}
            onMafiaVote={handleMafiaVote}
            onDoctorProtect={handleDoctorProtect}
            onDetectiveInvestigate={handleDetectiveInvestigate}
            onDayVote={handleDayVote}
            onChatMessage={handleChatMessage}
            phaseTimeRemaining={phaseTimeRemaining}
            investigationResults={investigationResults}
            myNightSelection={myNightSelection}
            myDayVote={myDayVote}
          />
        );

      case 'gameOver':
        if (!gameResult) return null;
        return (
          <MafiaGameOver
            result={gameResult}
            onPlayAgain={handlePlayAgain}
            onExit={handleExit}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="mafia-game-container" role="main" aria-label="Mafia Game">
      {view === 'modeSelect' ? (
        <div className="mafia-game-content">
          {renderView()}
        </div>
      ) : view === 'lobby' || view === 'roleReveal' || view === 'gameOver' ? (
        <>
          <div className="mafia-game-content">
            {renderView()}
          </div>
          <MafiaInstructions isOpen={showInstructions} onClose={() => setShowInstructions(false)} />
        </>
      ) : (
        (() => {
          const layoutConfig: UniversalGameLayoutConfig = {
            gameName: 'Mafia',
            board: (
              <div className="mafia-game-content">
                {renderView()}
              </div>
            ),
            statusBar: {
              turnIndicator: (
                <span>
                  {currentPhase === 'night' ? '🌙 Night Phase' : currentPhase === 'dayDiscussion' || currentPhase === 'dayVoting' ? '☀️ Day Phase' : `Phase: ${currentPhase}`}
                </span>
              ),
              gameStatus: `Round ${gameState?.cycleNumber || 1}`,
              gameStatusType: 'active',
              connectionStatus: connectionStatus,
              roomCode: roomCode || undefined,
            },
            actions: [
              { id: 'rules', label: 'Rules', icon: '📖', onClick: () => setShowInstructions(true), variant: 'secondary' },
              { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
            ],
            roomCode: roomCode || undefined,
          };

          return (
            <>
              <UniversalGameLayout config={layoutConfig} />
              <MafiaInstructions isOpen={showInstructions} onClose={() => setShowInstructions(false)} />
            </>
          );
        })()
      )}
    </div>
  );
};

// Exported component wrapped in ErrorBoundary
export const MafiaGame: React.FC<MafiaGameProps> = (props) => {
  return (
    <ErrorBoundary componentName="Mafia">
      <MafiaGameInner {...props} />
    </ErrorBoundary>
  );
};

export default MafiaGame;
